import { createContext, useContext, useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Camera, Mic, FileText, File, LinkIcon, MapPin, X, CheckCircle2 } from "@/lib/icons";

const CaptureContext = createContext(null);
export const useCapture = () => useContext(CaptureContext);

const TABS = [
  { key: "note", label: "Note", icon: FileText },
  { key: "photo", label: "Photo", icon: Camera },
  { key: "audio", label: "Voice", icon: Mic },
  { key: "file", label: "File", icon: File },
  { key: "link", label: "Link", icon: LinkIcon },
];

export function CaptureProvider({ children, onSaved }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("note");
  const [collections, setCollections] = useState([]);
  const openCapture = (initialTab = "note") => {
    setTab(initialTab);
    setOpen(true);
  };

  useEffect(() => {
    if (open) {
      api.get("/collections").then((r) => setCollections(r.data.collections)).catch(() => {});
    }
  }, [open]);

  return (
    <CaptureContext.Provider value={{ openCapture }}>
      {children}
      <CapturePanel
        open={open}
        setOpen={setOpen}
        tab={tab}
        setTab={setTab}
        collections={collections}
        onSaved={onSaved}
      />
    </CaptureContext.Provider>
  );
}

function CapturePanel({ open, setOpen, tab, setTab, collections, onSaved }) {
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [tags, setTags] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [collectionId, setCollectionId] = useState("none");
  const [location, setLocation] = useState(null);
  const [blob, setBlob] = useState(null);
  const [blobName, setBlobName] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);

  // camera / mic
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [cameraOn, setCameraOn] = useState(false);
  const [recording, setRecording] = useState(false);
  const [mediaError, setMediaError] = useState("");

  const reset = () => {
    setTitle(""); setText(""); setTags(""); setSourceUrl(""); setCollectionId("none");
    setLocation(null); setBlob(null); setBlobName(""); setPreviewUrl(null);
    setProgress(0); setMediaError("");
    stopCamera(); stopRecording(true);
  };

  const hasContent = () => title || text || sourceUrl || blob;

  const requestClose = (val) => {
    if (!val && hasContent() && !saving) {
      if (!window.confirm("Discard this unsaved capture?")) return;
    }
    if (!val) reset();
    setOpen(val);
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraOn(false);
  };

  const startCamera = async () => {
    setMediaError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      setCameraOn(true);
      setTimeout(() => {
        if (videoRef.current) videoRef.current.srcObject = stream;
      }, 50);
    } catch (e) {
      setMediaError("Camera unavailable or permission denied. Use 'Upload a photo' instead.");
    }
  };

  const snapPhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((b) => {
      setBlob(b);
      setBlobName(`photo-${Date.now()}.jpg`);
      setPreviewUrl(URL.createObjectURL(b));
      stopCamera();
    }, "image/jpeg", 0.92);
  };

  const startRecording = async () => {
    setMediaError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const rec = new MediaRecorder(stream);
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = () => {
        const b = new Blob(chunksRef.current, { type: "audio/webm" });
        setBlob(b);
        setBlobName(`recording-${Date.now()}.webm`);
        setPreviewUrl(URL.createObjectURL(b));
        if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (e) {
      setMediaError("Microphone unavailable or permission denied. Use 'Upload audio' instead.");
    }
  };

  const stopRecording = (silent) => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    setRecording(false);
    if (silent && streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBlob(f);
    setBlobName(f.name);
    if (f.type.startsWith("image/")) setPreviewUrl(URL.createObjectURL(f));
    else setPreviewUrl(null);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ""));
  };

  const getLocation = () => {
    if (!navigator.geolocation) { setMediaError("Geolocation not supported."); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude, label: "Current location" }),
      () => setMediaError("Location permission denied — capture still works without it.")
    );
  };

  const saveOffline = () => {
    const drafts = JSON.parse(localStorage.getItem("dl_pending_drafts") || "[]");
    drafts.push({ id: crypto.randomUUID(), title, text, tags, created_at: new Date().toISOString() });
    localStorage.setItem("dl_pending_drafts", JSON.stringify(drafts));
    toast.info("Saved locally (pending). It will sync when you're back online.");
    window.dispatchEvent(new Event("dl-pending-changed"));
    reset();
    setOpen(false);
  };

  const save = async () => {
    if (tab === "note" && !title && !text) { toast.error("Add a title or some text first."); return; }
    if (tab === "link" && !sourceUrl) { toast.error("Enter a URL to save the link."); return; }
    if (["photo", "audio", "file"].includes(tab) && !blob) { toast.error("Attach a file first."); return; }

    if (tab === "note" && !navigator.onLine) { saveOffline(); return; }

    setSaving(true);
    setProgress(blob ? 5 : 100);
    try {
      const payload = {
        title: title || blobName || (tab === "link" ? sourceUrl : "Untitled capture"),
        text,
        type: tab,
        source_url: tab === "link" ? sourceUrl : null,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        collection_ids: collectionId !== "none" ? [collectionId] : [],
        location,
        client_token: crypto.randomUUID(),
      };
      const { data: capture } = await api.post("/captures", payload);
      if (blob) {
        const form = new FormData();
        form.append("file", blob, blobName || "upload");
        const res = await api.post(`/captures/${capture.id}/attachments`, form, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => setProgress(Math.round((e.loaded / (e.total || 1)) * 100)),
        });
        if (res.data.duplicate) {
          toast.warning("Exact duplicate detected — both observations were kept.");
        }
      }
      toast.success("Captured. Saved to your Loading Dock.", { icon: null });
      reset();
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed. Your capture was not saved — retry.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={requestClose}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 border-l overflow-y-auto dl-scroll"
        style={{ background: "var(--dl-sidebar)", borderColor: "var(--dl-border)" }}
        data-testid="capture-panel"
      >
        <SheetHeader className="p-5 border-b" style={{ borderColor: "var(--dl-border)" }}>
          <SheetTitle className="font-display text-xl text-white flex items-center gap-2">
            <span className="h-2 w-2 rounded-full" style={{ background: "var(--c-capture)" }} />
            Capture
          </SheetTitle>
          <p className="text-xs text-slate-400">Save first. Organize later — it lands in your Loading Dock.</p>
        </SheetHeader>

        <div className="p-5 space-y-5">
          <div className="grid grid-cols-5 gap-1.5">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  data-testid={`capture-tab-${t.key}`}
                  onClick={() => setTab(t.key)}
                  className="flex flex-col items-center gap-1 py-2.5 rounded-lg text-[11px] font-medium transition-colors"
                  style={{
                    background: active ? "rgba(37,99,235,0.18)" : "transparent",
                    color: active ? "#60A5FA" : "#94A3B8",
                    border: `1px solid ${active ? "rgba(37,99,235,0.4)" : "transparent"}`,
                  }}
                >
                  <Icon size={18} />
                  {t.label}
                </button>
              );
            })}
          </div>

          {tab === "photo" && (
            <div className="space-y-2">
              {cameraOn ? (
                <div className="space-y-2">
                  <video ref={videoRef} autoPlay playsInline muted className="w-full rounded-lg bg-black" />
                  <div className="flex gap-2">
                    <Button data-testid="snap-photo-btn" onClick={snapPhoto} className="flex-1">Take Photo</Button>
                    <Button variant="outline" onClick={stopCamera}>Cancel</Button>
                  </div>
                </div>
              ) : previewUrl ? (
                <img src={previewUrl} alt="preview" className="w-full rounded-lg" />
              ) : (
                <div className="flex gap-2">
                  <Button data-testid="start-camera-btn" onClick={startCamera} variant="outline" className="flex-1 gap-2">
                    <Camera size={16} /> Use Camera
                  </Button>
                  <label className="flex-1">
                    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="photo-file-input" />
                    <span className="flex items-center justify-center gap-2 h-10 rounded-md border border-input cursor-pointer text-sm hover:bg-white/5">Upload a photo</span>
                  </label>
                </div>
              )}
            </div>
          )}

          {tab === "audio" && (
            <div className="space-y-2">
              {previewUrl ? (
                <audio src={previewUrl} controls className="w-full" />
              ) : recording ? (
                <Button data-testid="stop-record-btn" onClick={() => stopRecording(false)} variant="destructive" className="w-full gap-2">
                  <span className="dl-rec h-2.5 w-2.5 rounded-full bg-white" /> Stop Recording
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button data-testid="start-record-btn" onClick={startRecording} variant="outline" className="flex-1 gap-2">
                    <Mic size={16} /> Record
                  </Button>
                  <label className="flex-1">
                    <input type="file" accept="audio/*" className="hidden" onChange={onFile} data-testid="audio-file-input" />
                    <span className="flex items-center justify-center gap-2 h-10 rounded-md border border-input cursor-pointer text-sm hover:bg-white/5">Upload audio</span>
                  </label>
                </div>
              )}
              <p className="text-[11px] text-slate-500">A recording is saved even when transcription is unavailable.</p>
            </div>
          )}

          {tab === "file" && (
            <label className="block">
              <input type="file" className="hidden" onChange={onFile} data-testid="file-input" />
              <span className="flex flex-col items-center justify-center gap-2 h-28 rounded-lg border border-dashed cursor-pointer text-sm text-slate-400 hover:bg-white/5" style={{ borderColor: "var(--dl-border)" }}>
                <File size={22} />
                {blobName || "Click to choose a PDF or any file"}
              </span>
            </label>
          )}

          {tab === "link" && (
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs">URL</Label>
              <Input data-testid="link-url-input" value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} placeholder="https://…" className="bg-black/20" />
              <p className="text-[11px] text-slate-500">We save the URL and your note. Remote page contents are not fetched in this release.</p>
            </div>
          )}

          {blobName && tab !== "photo" && (
            <div className="flex items-center justify-between text-xs text-slate-300 bg-black/20 rounded-md px-3 py-2">
              <span className="truncate">{blobName}</span>
              <button onClick={() => { setBlob(null); setBlobName(""); setPreviewUrl(null); }}><X size={14} /></button>
            </div>
          )}

          {mediaError && <p className="text-xs text-amber-400">{mediaError}</p>}

          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Title</Label>
            <Input data-testid="capture-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Optional title" className="bg-black/20" />
          </div>

          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">{tab === "link" ? "Annotation" : "Note"}</Label>
            <Textarea data-testid="capture-text-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="What should you remember about this?" className="bg-black/20 min-h-[90px]" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs">Collection</Label>
              <Select value={collectionId} onValueChange={setCollectionId}>
                <SelectTrigger data-testid="capture-collection-select" className="bg-black/20"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {collections.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs">Tags</Label>
              <Input data-testid="capture-tags-input" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="comma, separated" className="bg-black/20" />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={getLocation} className="gap-1.5 text-slate-300" data-testid="capture-location-btn">
              <MapPin size={14} /> {location ? `${location.lat.toFixed(3)}, ${location.lng.toFixed(3)}` : "Add location"}
            </Button>
            {location && <button onClick={() => setLocation(null)} className="text-xs text-slate-500">clear</button>}
          </div>

          {saving && blob && <Progress value={progress} className="h-1.5" />}

          <Button data-testid="save-capture-btn" onClick={save} disabled={saving} className="w-full capture-glow gap-2" style={{ background: "var(--c-capture)" }}>
            <CheckCircle2 size={16} /> {saving ? "Saving…" : "Save capture"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
